import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getContentSync, createTip, updateTip, deleteTip } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check, X, Loader2, Calendar } from 'lucide-react';

const CATEGORIES = ['nutrition', 'exercise', 'lifestyle', 'general'];

const DailyTipsPage = () => {
  const location = useLocation();
  const [tips, setTips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTip, setNewTip] = useState({ text: '', category: 'nutrition', displayOrder: 1, isActive: true });
  const [showCalendar, setShowCalendar] = useState(false);

  const fetchTips = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getContentSync();
      const sortedTips = [...response.data.dailyTips].sort((a, b) => a.displayOrder - b.displayOrder);
      setTips(sortedTips);
    } catch (error) {
      toast.error('Failed to load tips');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTips();
  }, [fetchTips]);

  useEffect(() => {
    if (location.state?.openNew) {
      setShowNewForm(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const startEditing = (tip) => {
    setEditingId(tip.id);
    setEditingData({ ...tip });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingData(null);
  };

  const saveEdit = async () => {
    if (!editingData.text.trim()) {
      toast.error('Tip text is required');
      return;
    }

    setIsSaving(true);
    try {
      await updateTip(editingId, editingData);
      toast.success('Tip updated successfully');
      setEditingId(null);
      setEditingData(null);
      fetchTips();
    } catch (error) {
      toast.error('Error saving — try again');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    setIsSaving(true);
    try {
      await deleteTip(deletingId);
      toast.success('Tip deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingId(null);
      fetchTips();
    } catch (error) {
      toast.error('Error deleting — try again');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateNew = async () => {
    if (!newTip.text.trim()) {
      toast.error('Tip text is required');
      return;
    }

    setIsSaving(true);
    try {
      const maxOrder = Math.max(0, ...tips.map(t => t.displayOrder || 0));
      await createTip({ ...newTip, displayOrder: maxOrder + 1 });
      toast.success('Tip created successfully');
      setShowNewForm(false);
      setNewTip({ text: '', category: 'nutrition', displayOrder: 1, isActive: true });
      fetchTips();
    } catch (error) {
      toast.error('Error creating — try again');
    } finally {
      setIsSaving(false);
    }
  };

  const getTipForDay = (dayOfYear) => {
    const activeTips = tips.filter(t => t.isActive);
    if (activeTips.length === 0) return null;
    return activeTips[dayOfYear % activeTips.length];
  };

  return (
    <div className="p-6 space-y-4" data-testid="tips-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Daily Tips</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowCalendar(!showCalendar)}
          >
            <Calendar size={16} className="mr-1" /> Preview
          </Button>
          <Button 
            onClick={() => setShowNewForm(true)}
            className="bg-[#1A6B3C] hover:bg-[#155a32]"
            data-testid="new-tip-btn"
          >
            <Plus size={16} className="mr-1" /> New Tip
          </Button>
        </div>
      </div>

      {/* Calendar Preview */}
      {showCalendar && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3">Day Rotation Preview (365-day cycle)</h3>
            <div className="grid grid-cols-7 gap-2 max-h-[300px] overflow-y-auto">
              {[...Array(30)].map((_, i) => {
                const tip = getTipForDay(i + 1);
                return (
                  <div key={i} className="p-2 bg-gray-50 rounded text-center">
                    <div className="text-xs text-gray-400">Day {i + 1}</div>
                    <div className="text-lg">{tip ? '💡' : '-'}</div>
                    <div className="text-xs truncate" title={tip?.text}>
                      {tip?.text?.slice(0, 20)}...
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* New Tip Form */}
      {showNewForm && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3">Create New Tip</h3>
            <div className="space-y-3">
              <div>
                <Label>Tip Text *</Label>
                <Input
                  value={newTip.text}
                  onChange={(e) => setNewTip({ ...newTip, text: e.target.value })}
                  placeholder="Enter tip text..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={newTip.category} onValueChange={(v) => setNewTip({ ...newTip, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch checked={newTip.isActive} onCheckedChange={(v) => setNewTip({ ...newTip, isActive: v })} />
                  <Label>Active</Label>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleCreateNew} disabled={isSaving} className="bg-[#1A6B3C] hover:bg-[#155a32]">
                  {isSaving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Create
                </Button>
                <Button variant="outline" onClick={() => setShowNewForm(false)}>Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tips List */}
      <div className="space-y-3">
        {isLoading ? (
          [...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)
        ) : tips.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-500 mb-4">No tips found</p>
              <Button onClick={() => setShowNewForm(true)} className="bg-[#1A6B3C] hover:bg-[#155a32]">
                <Plus size={16} className="mr-1" /> Add First Tip
              </Button>
            </CardContent>
          </Card>
        ) : (
          tips.map((tip) => (
            <Card key={tip.id} data-testid={`tip-card-${tip.id}`}>
              <CardContent className="p-4">
                {editingId === tip.id ? (
                  <div className="space-y-3">
                    <Input
                      value={editingData.text}
                      onChange={(e) => setEditingData({ ...editingData, text: e.target.value })}
                      className="font-medium"
                    />
                    <div className="grid grid-cols-3 gap-4">
                      <Select value={editingData.category} onValueChange={(v) => setEditingData({ ...editingData, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        value={editingData.displayOrder}
                        onChange={(e) => setEditingData({ ...editingData, displayOrder: parseInt(e.target.value) || 1 })}
                        placeholder="Order"
                      />
                      <div className="flex items-center gap-2">
                        <Switch checked={editingData.isActive} onCheckedChange={(v) => setEditingData({ ...editingData, isActive: v })} />
                        <Label>Active</Label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveEdit} disabled={isSaving} className="bg-[#1A6B3C] hover:bg-[#155a32]">
                        <Check size={14} className="mr-1" /> Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEditing}>
                        <X size={14} className="mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">💡</span>
                        <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                          tip.category === 'nutrition' ? 'bg-green-100 text-green-700' :
                          tip.category === 'exercise' ? 'bg-blue-100 text-blue-700' :
                          tip.category === 'lifestyle' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {tip.category}
                        </span>
                        <span className="text-xs text-gray-400">Order: {tip.displayOrder}</span>
                        {!tip.isActive && <span className="text-xs text-red-500">Inactive</span>}
                      </div>
                      <p className="text-gray-800">{tip.text}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => startEditing(tip)}>
                        <Pencil size={16} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => { setDeletingId(tip.id); setDeleteDialogOpen(true); }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DailyTipsPage;
